import type { ReactNode } from 'react'
import { DotsVerticalIcon, ThumbsUpIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { PostAttachmentsProvider } from 'modules/attachments/components/context'
import AttachmentsPreview from 'modules/attachments/components/preview'
import type { AddCommentRequestType } from 'modules/comments/api/types'
import { TextWithMentions } from 'modules/comments/components/comment-editor/deserialize-mention-to-html'
import { useCommentsContext } from 'modules/comments/components/comments-list'
import type { CommentsTypeEnum } from 'modules/comments/types'
import { isCommunityExtra } from 'modules/comments/utils/comments-extra-type-guards'
import useUserInfo from 'modules/community/hooks/use-user-info'
import { CommentHeader } from './ui/comment-header'
import { CommentLayout } from './ui/comment-layout'

export function SubmittedComment({
  variables,
  submittedAt,
  level,
}: {
  variables: AddCommentRequestType<CommentsTypeEnum>
  submittedAt: number
  level: 0 | 1
}): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()
  const { extra, readOnly } = useCommentsContext()
  const isCommunity = isCommunityExtra(extra)
  const { data: profile } = useUserInfo({
    userId: isAuthorizedUser(user) ? user.id : 0,
    shouldFetch: isCommunity && isAuthorizedUser(user),
  })
  if (!isAuthorizedUser(user)) return null

  const userName = profile?.displayName ?? [user.firstName, user.lastName].filter(Boolean).join(' ')
  const userAvatarUrl = profile?.profileImageUrl ?? user.avatarUrl

  return (
    <PostAttachmentsProvider attachments={variables.attachments}>
      <div className="relative" data-testid="comment-submission">
        <CommentLayout
          userName={userName}
          userAvatarUrl={userAvatarUrl}
          level={level}
          header={
            <CommentHeader
              userName={userName}
              userId={user.id}
              isCommunity={isCommunity}
              commentTimestamp={submittedAt}
              wasEdited={false}
              isPinned={false}
            />
          }
          footer={
            <div className="mt-1.5 flex translate-y-1 items-center gap-2 text-sm">
              <button
                type="button"
                data-testid="like-button"
                disabled
                aria-pressed={false}
                className="group relative inline-flex items-center gap-2 rounded-md px-1.5 py-2 text-text-tertiary *:transition-colors hover:bg-bg-accent/20"
              >
                <ThumbsUpIcon className="size-5 text-text-secondary group-hover:text-text-link" />
              </button>
              {!readOnly && (
                <button
                  type="button"
                  data-testid="comment-reply"
                  disabled
                  className="cursor-pointer font-medium text-text-secondary hover:text-text-link"
                >
                  {t('comments.components.comments.reply_label')}
                </button>
              )}
            </div>
          }
        >
          <TextWithMentions
            data-testid="comment-text"
            className="mt-1 text-start text-text-primary/70 [word-wrap:break-word] md:mt-4 [&_a]:text-text-link [&_a]:no-underline [&_a:hover]:underline [&_p]:m-0 [&_p]:p-0"
            text={variables.text}
            mentions={[]}
          />
          <AttachmentsPreview className={variables.attachments?.length ? 'pt-4' : undefined} />
          <button
            type="button"
            data-testid="comment-actions-trigger"
            disabled
            className="absolute top-3 right-3.5 justify-self-end"
          >
            <div className="relative">
              <DotsVerticalIcon className="size-5" />
            </div>
          </button>
        </CommentLayout>
      </div>
    </PostAttachmentsProvider>
  )
}
